import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo } from "react";
import { Doughnut } from "react-chartjs-2";
import getCustomLegend from "utils/ChartHelpers.js/CustomLegendPlugin";
import { cn } from "utils/common.utils";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
ChartJS.register(ArcElement, Tooltip, Legend);
/**
 * Custom Hook to configure Chart.js Doughnut options and return ready-to-use components.
 * This hook generates options immutably and memoizes the result for performance.
 *
 * @param {string} containerId - ID of the container element where the custom HTML legend will be placed.
 * @param {boolean} [isHalfChart=false] - If true, configures the chart for a half-doughnut display (rotation 180 deg).
 * @returns {{
 * DoughnutChart: function(props: {data: object}): JSX.Element,
 * DoughnutChartFooter: function(props: {className?: string}): JSX.Element
 * }} An object containing the configured Chart component and its corresponding legend container element.
 */
export function useDoughnutChart(containerId, isHalfChart = false) {
    // Use useMemo to ensure the options object is only re-created
    // when the dependencies (containerId or isHalfChart) change.
    const options = useMemo(() => {
        // --- 1. Define Base Configuration ---
        const baseCommonOptions = {
            responsive: true,
            maintainAspectRatio: false,
            cutout: "80%",
            radius: "100%",
            borderRadius: 1,
            borderJoinStyle: "miter",
            borderWidth: 0,
            spacing: 0,
            borderAlign: "center",
        };
        const basePlugins = {
            htmlLegend: {
                containerID: containerId,
            },
            legend: {
                display: false,
            },
            // Base tooltip setting, safely checking document direction
            // Also set color defaults (can be overridden via CSS variables)
            tooltip: {
                ltr: typeof document !== "undefined" && document.body.dir === "rtl"
                    ? false
                    : true,
            },
        };
        // --- 2. Create Initial Options Object (Full Copy) ---
        let finalOptions = Object.assign(Object.assign({}, baseCommonOptions), { plugins: Object.assign({}, basePlugins) });
        // --- 3. Apply Half-Chart Specific Overrides (Immutable Modification) ---
        if (isHalfChart) {
            finalOptions.rotation = -90;
            finalOptions.circumference = 180;
            finalOptions.cutout = "85%";
            finalOptions.radius = "100%";
            // Deeply clone and override the tooltip filter
            finalOptions.plugins = Object.assign(Object.assign({}, finalOptions.plugins), { tooltip: Object.assign(Object.assign({}, finalOptions.plugins.tooltip), { filter: function (item) {
                        return (item === null || item === void 0 ? void 0 : item.dataIndex) === 0;
                    } }) });
        }
        return finalOptions;
    }, [containerId, isHalfChart]);
    const _hoverOptions = {
        plugins: {
            tooltip: {
                // disable built-in tooltip rendering — we render externally
                enabled: false,
                displayColors: false,
                // keep the label callback to format "Label: value"
                callbacks: {
                    label: function (context) {
                        const label = context.label || "";
                        const value = context.parsed !== undefined ? context.parsed : context.raw;
                        return `${label}: ${value}`;
                    },
                },
                // external tooltip renderer (hardcoded colors, fully opaque background)
                external: function (context) {
                    const { chart, tooltip } = context;
                    const canvasParent = chart.canvas && chart.canvas.parentElement;
                    if (!canvasParent)
                        return;
                    // ensure parent is positioned for absolute placement
                    if (!canvasParent.style.position)
                        canvasParent.style.position = "relative";
                    let tooltipEl = canvasParent.querySelector('.chartjs-external-tooltip');
                    if (!tooltipEl) {
                        tooltipEl = document.createElement('div');
                        tooltipEl.className = 'chartjs-external-tooltip';
                        // Fully opaque grey background + white text
                        tooltipEl.style.position = 'absolute';
                        tooltipEl.style.pointerEvents = 'none';
                        tooltipEl.style.background = 'rgba(0,0,0,0.8)'; // fully opaque (alpha = 1)
                        tooltipEl.style.color = '#ffffff';
                        tooltipEl.style.padding = '6px 8px';
                        tooltipEl.style.borderRadius = '6px';
                        tooltipEl.style.fontSize = '12px';
                        tooltipEl.style.whiteSpace = 'nowrap';
                        tooltipEl.style.transition = 'opacity 0.12s ease, transform 0.12s ease';
                        tooltipEl.style.zIndex = '9999';
                        tooltipEl.style.border = '1px solid rgba(255,255,255,0.08)';
                        tooltipEl.style.boxShadow = '0 6px 18px rgba(0,0,0,0.5)';
                        // create an arrow element inside the tooltip
                        const arrowEl = document.createElement('div');
                        arrowEl.className = 'chartjs-external-tooltip-arrow';
                        arrowEl.style.position = 'absolute';
                        arrowEl.style.width = '0px';
                        arrowEl.style.height = '0px';
                        arrowEl.style.pointerEvents = 'none';
                        tooltipEl.appendChild(arrowEl);
                        canvasParent.appendChild(tooltipEl);
                    }
                    // Hide when tooltip is not visible
                    if (!tooltip || tooltip.opacity === 0) {
                        tooltipEl.style.opacity = '0';
                        tooltipEl.style.visibility = 'hidden';
                        return;
                    }
                    // Build tooltip content using formatted body lines
                    const lines = [];
                    if (tooltip.body) {
                        tooltip.body.forEach(function (bodyItem) {
                            const text = bodyItem.lines.join(' ');
                            lines.push(text);
                        });
                    }
                    tooltipEl.innerHTML = lines.map((l) => `<div>${l}</div>`).join('');
                    // Position tooltip near the caret position with smart left/right placement
                    const caretX = tooltip.caretX || chart.width / 2;
                    const caretY = tooltip.caretY || chart.height / 2;
                    // reset to measure
                    tooltipEl.style.opacity = '1';
                    tooltipEl.style.visibility = 'visible';
                    tooltipEl.style.left = '0px';
                    tooltipEl.style.top = '0px';
                    const elRect = tooltipEl.getBoundingClientRect();
                    const parentRect = canvasParent.getBoundingClientRect();
                    const arrowSize = 6;
                    const arrowOffset = 8; // space between tooltip and caret
                    // Calculate available space on left and right
                    const spaceOnRight = parentRect.width - caretX;
                    const spaceOnLeft = caretX;
                    const tooltipWidth = elRect.width + arrowSize;
                    const tooltipHeight = elRect.height;
                    // Choose placement based on available space
                    let left, placedRight;
                    if (spaceOnRight >= tooltipWidth + arrowOffset) {
                        // Place on right side
                        left = caretX + arrowOffset;
                        placedRight = true;
                    }
                    else if (spaceOnLeft >= tooltipWidth + arrowOffset) {
                        // Place on left side
                        left = caretX - tooltipWidth - arrowOffset;
                        placedRight = false;
                    }
                    else {
                        // Fallback: place where there's more space
                        if (spaceOnRight >= spaceOnLeft) {
                            left = Math.min(caretX + arrowOffset, parentRect.width - tooltipWidth);
                            placedRight = true;
                        }
                        else {
                            left = Math.max(caretX - tooltipWidth - arrowOffset, 0);
                            placedRight = false;
                        }
                    }
                    // Vertical positioning: center on caret, with bounds checking
                    let top = caretY - tooltipHeight / 2;
                    if (top < 4)
                        top = 4;
                    if (top + tooltipHeight > parentRect.height)
                        top = parentRect.height - tooltipHeight - 4;
                    tooltipEl.style.left = `${Math.round(left)}px`;
                    tooltipEl.style.top = `${Math.round(top)}px`;
                    // position and style arrow inside tooltip
                    const arrowEl = tooltipEl.querySelector('.chartjs-external-tooltip-arrow');
                    if (arrowEl) {
                        // clear previous borders
                        arrowEl.style.borderTop = '';
                        arrowEl.style.borderBottom = '';
                        arrowEl.style.borderLeft = '';
                        arrowEl.style.borderRight = '';
                        arrowEl.style.left = 'auto';
                        arrowEl.style.right = 'auto';
                        if (placedRight) {
                            // left-pointing triangle (arrow points back to caret on the left)
                            arrowEl.style.left = `-${arrowSize}px`;
                            arrowEl.style.borderTop = `${arrowSize}px solid transparent`;
                            arrowEl.style.borderBottom = `${arrowSize}px solid transparent`;
                            arrowEl.style.borderRight = `${arrowSize}px solid rgba(75,85,99,1)`;
                        }
                        else {
                            // right-pointing triangle (arrow points back to caret on the right)
                            arrowEl.style.right = `-${arrowSize}px`;
                            arrowEl.style.borderTop = `${arrowSize}px solid transparent`;
                            arrowEl.style.borderBottom = `${arrowSize}px solid transparent`;
                            arrowEl.style.borderLeft = `${arrowSize}px solid rgba(75,85,99,1)`;
                        }
                        // Vertical center of arrow
                        arrowEl.style.top = `${Math.round(tooltipHeight / 2 - arrowSize)}px`;
                    }
                },
            },
        },
    };
    return {
        DoughnutChart: ({ data, options: overrideOptions }) => {
            const _overrideOptions = overrideOptions || _hoverOptions || {};
            const mergedOptions = Object.assign(Object.assign(Object.assign({}, options), (_overrideOptions || {})), { plugins: Object.assign(Object.assign({}, (options.plugins || {})), ((_overrideOptions && _overrideOptions.plugins) || {})) });
            return (_jsx(Doughnut, { data: data, options: mergedOptions, plugins: [getCustomLegend()] }));
        },
        DoughnutChartFooter: ({ className }) => (_jsx("div", { id: containerId, className: cn("mt-8", className) })),
    };
}
